import React from "react";
import { cx } from "utils/join-classnames";
import { Channel } from "core/models";
import usePermissions from "ui/hooks/usePermissions";
import {
    Button,
    StateSwitch,
    Icon,
    DateSelection,
    PopOver,
    Tooltip,
} from "ui/components";
import { useLivestreamContext } from "ui/context/LivestreamContext";
import { useEditorContext } from "ui/context/EditorContext";
import { useSessionContext } from "ui/context/SessionContext";
import { useNotifications } from "ui/context/NotificationsContext";
import { getConfigByKeyMerged } from "core/services/site/configValue";
import styles from "./EditorNavigation.module.scss";

const DEFAULT_MAX_DURATION = 1800;
interface Props {
    onSaveSelections(): void;
    channel: Channel;
}

const EditorPageNavigation: React.FC<Props> = ({
    channel,
    onSaveSelections,
}) => {
    const sessionContext = useSessionContext();
    const editorContext = useEditorContext();
    const userPermissions = usePermissions();
    const { addNotification } = useNotifications();
    const [maxClipDuration, setMaxClipDuration] = React.useState<number | null>(
        null,
    );
    const { addChannel } = useLivestreamContext();

    const toggleShowTimestamps = () => {
        editorContext.setShowTimestamps(!editorContext.showTimestamps);
    };

    const toggleShowDurations = () => {
        editorContext.setShowDurations(!editorContext.showDurations);
    };

    const openStream = (event: React.MouseEvent) => {
        event.preventDefault();
        if (channel.exStream.online === false) {
            return;
        }
        addChannel(channel);
    };

    const userCanWatchLivestream = userPermissions.canWatchLive;

    const isSelectionEmpty = (): boolean => {
        for (let i = 0; i < editorContext.selections.length; i += 1) {
            if (editorContext.selections[i].segments.length > 0) {
                return false;
            }
        }
        return true;
    };

    const onSelectDate = (newDate: Date) => {
        editorContext.setChunkContainerDisplayDate(newDate);
        editorContext.setChunkContainerNavigationDate(newDate);
    };

    const checkMaxDuration = async () => {
        try {
            if (maxClipDuration) {
                return;
            }
            if (!sessionContext.session || !sessionContext.selectedProject) {
                return;
            }
            const result = await getConfigByKeyMerged<number>(
                sessionContext.session.token,
                "fragmentfromselection_maxduration",
                sessionContext.selectedProject.id,
            );
            if (result.error || !result.value) {
                throw Error(result.error);
            }
            setMaxClipDuration(result.value.value || DEFAULT_MAX_DURATION);
        } catch (err) {
            addNotification(
                "error",
                "API Error",
                `couldn't get max duration value`,
                5000,
            );
        }
    };

    React.useEffect(() => {
        checkMaxDuration();
    }, []);

    React.useEffect(() => {
        if (
            maxClipDuration &&
            editorContext.selectionsDuration > maxClipDuration
        ) {
            addNotification(
                "error",
                "Clip duration too long",
                `Should be less than ${maxClipDuration} seconds`,
                6000,
            );
        }
    }, [editorContext.selectionsDuration]);

    const canSave = isSelectionEmpty() === false;

    const getWatchLiveTooltip = (): string => {
        let text = "Watch the stream live";
        if (!channel.exStream.online) {
            text = "stream is offline";
        }
        if (!userCanWatchLivestream) {
            text = "not available in your current plan";
        }
        return text;
    };

    return (
        <div className={styles.navigation}>
            <div className={styles.item}>
                <PopOver type="icon" icon="settings" orientation="left">
                    <>
                        <div className={styles.settingItem}>
                            <div
                                role="presentation"
                                onClick={toggleShowTimestamps}
                            >
                                Show timestamp
                            </div>
                            <div>
                                <StateSwitch
                                    status={editorContext.showTimestamps}
                                    onChange={toggleShowTimestamps}
                                />
                            </div>
                        </div>
                        <div className={styles.settingItem}>
                            <div
                                role="presentation"
                                onClick={toggleShowDurations}
                            >
                                Show Duration
                            </div>
                            <div>
                                <StateSwitch
                                    status={editorContext.showDurations}
                                    onChange={toggleShowDurations}
                                />
                            </div>
                        </div>
                    </>
                </PopOver>
            </div>
            <div className={styles.item}>
                <hr />
            </div>
            <div className={styles.item}>
                <DateSelection
                    onlineMinutes={channel ? channel.exStream.onlineMinutes : 0}
                    currentDate={editorContext.chunkContainerDisplayDate}
                    onSelectDate={onSelectDate}
                />
            </div>
            <div className={styles.item}>
                <hr />
            </div>
            <div className={styles.item}>
                {channel && (
                    <Button
                        type="secondary"
                        tooltip={getWatchLiveTooltip()}
                        theme="light"
                        icon="preview"
                        onClick={openStream}
                        disabled={
                            !channel.exStream.online || !userCanWatchLivestream
                        }
                    >
                        Watch live
                    </Button>
                )}
            </div>
            <div className={styles.item}>
                <hr />
            </div>
            {channel && (
                <div className={styles.name}>
                    <Tooltip text="Channel name">
                        <p>{channel.name}</p>
                    </Tooltip>
                </div>
            )}
            <div className={styles.spacer} />
            <div className={styles.item}>
                <Button
                    type="secondary"
                    theme="light"
                    className={cx(
                        styles.refreshBtn,
                        editorContext.liveMode ? styles.refreshBtn__live : null,
                    )}
                    onClick={() =>
                        editorContext.setLiveMode(!editorContext.liveMode)
                    }
                >
                    {editorContext.liveMode ? (
                        <>
                            <Icon
                                name="autorefresh"
                                className={styles.refreshBtn__icon}
                            />{" "}
                            Live
                        </>
                    ) : (
                        "Go to live"
                    )}
                </Button>
            </div>
            <div className={styles.item}>
                <hr />
            </div>
            <div className={styles.item}>
                <Button
                    type="primary"
                    disabled={
                        !canSave ||
                        (maxClipDuration || 60000) <
                            editorContext.selectionsDuration ||
                        !userPermissions.canCreateFragment
                    }
                    className={styles.button}
                    onClick={onSaveSelections}
                >
                    Create clip
                </Button>
            </div>
        </div>
    );
};

export default React.memo(EditorPageNavigation);
